import {
  useEffect,
  useCallback,
  MutableRefObject,
  useState,
  useRef,
} from "react";
import { useReactFlow } from "@xyflow/react";
import { useAtomValue, useSetAtom } from "jotai";
import {
  isViewOnlyModeAtom,
  createImageNodeSetter,
  createNotificationSetter,
  emitUserInteractionEventSetter,
} from "@zapier/canvas-state";
import { MAX_IMAGE_SIZE, MAX_IMAGE_SIZE_MB } from "@zapier/canvas-constants";

/**
 * Supported image MIME types matching backend validation
 * See: canvasbe/backend/canvasbe/canvas/views.py ALLOWED_TYPES
 */
const SUPPORTED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/avif",
  "image/svg+xml",
]);

/**
 * Check if a drag event contains files.
 */
const isFileDrag = (e: DragEvent): boolean => {
  return Array.from(e.dataTransfer?.types ?? []).includes("Files");
};

/**
 * Process image files and insert them as nodes with offsets to prevent overlap
 */
function processImageFiles(
  files: File[],
  getPosition: (index: number) => { x: number; y: number },
  handleImageFile: (
    file: File,
    clientX: number,
    clientY: number,
    uploadMethod?: "drag_and_drop" | "copy_and_paste",
  ) => void,
  createNotification: (notification: {
    type: "error";
    message: string;
  }) => void,
  emitUserInteractionEvent: (params: {
    eventAction: string;
    interactionGoal: string;
    rawText?: string;
    optionSelected?: string;
  }) => void,
  uploadMethod?: "drag_and_drop" | "copy_and_paste",
) {
  // Validate each file - show error for unsupported files
  const supportedFiles: File[] = [];
  files.forEach((file) => {
    const contentType = file.type.toLowerCase();
    const isImage = file.type.startsWith("image/");
    const isSupportedImage = isImage && SUPPORTED_IMAGE_TYPES.has(contentType);
    const eventAction =
      uploadMethod === "drag_and_drop" ? "drag_and_drop" : "click";

    if (!isSupportedImage) {
      // Track file type validation error for analytics
      emitUserInteractionEvent({
        eventAction,
        interactionGoal: "Image upload - unsupported file type",
        rawText: `File size: ${(file.size / 1024 / 1024).toFixed(2)} MB`,
        optionSelected: `File type: ${file.type || "unknown"}`,
      });

      createNotification({
        type: "error",
        message:
          "This file type isn't supported. Please upload a supported image format like PNG, JPG, SVG, WebP, AVIF, or GIF.",
      });
      return;
    }

    supportedFiles.push(file);
  });

  if (supportedFiles.length === 0) return;

  // Process only supported files
  supportedFiles.forEach((file, index) => {
    const { x, y } = getPosition(index);
    handleImageFile(file, x, y, uploadMethod);
  });
}

/**
 * Handle image insertion from drag/paste events
 */
function handleImageInsert(
  e: DragEvent | ClipboardEvent,
  getFiles: (e: DragEvent | ClipboardEvent) => File[] | null,
  getPosition: (index: number) => { x: number; y: number },
  handleImageFile: (
    file: File,
    clientX: number,
    clientY: number,
    uploadMethod?: "drag_and_drop" | "copy_and_paste",
  ) => void,
  createNotification: (notification: {
    type: "error";
    message: string;
  }) => void,
  emitUserInteractionEvent: (params: {
    eventAction: string;
    interactionGoal: string;
    rawText?: string;
    optionSelected?: string;
  }) => void,
  uploadMethod?: "drag_and_drop" | "copy_and_paste",
) {
  const files = getFiles(e);
  if (!files || files.length === 0) return;

  // Only prevent default if we have files to process
  e.preventDefault();
  e.stopPropagation();

  processImageFiles(
    files,
    getPosition,
    handleImageFile,
    createNotification,
    emitUserInteractionEvent,
    uploadMethod,
  );
}

/**
 * Hook to handle image insertion functionality
 * Supports:
 * - Dragging images from desktop onto the canvas
 * - Pasting images via Ctrl+V/Cmd+V
 * @returns Boolean indicating if files are being dragged over the canvas
 */
export function useImageInsert(
  ref?: MutableRefObject<HTMLDivElement | null>,
): boolean {
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const createImageNode = useSetAtom(createImageNodeSetter);
  const createNotification = useSetAtom(createNotificationSetter);
  const emitUserInteractionEvent = useSetAtom(emitUserInteractionEventSetter);
  const { screenToFlowPosition } = useReactFlow();
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  // Counter to handle nested elements (dragenter/dragleave fire on child elements)
  const dragCounterRef = useRef(0);

  const handleImageFile = useCallback(
    (
      file: File,
      clientX: number,
      clientY: number,
      uploadMethod?: "drag_and_drop" | "copy_and_paste",
    ) => {
      const eventAction =
        uploadMethod === "drag_and_drop" ? "drag_and_drop" : "click";
      // Validate file size (10MB limit)
      if (file.size > MAX_IMAGE_SIZE) {
        // Track file size validation error for analytics
        emitUserInteractionEvent({
          eventAction,
          interactionGoal: "Image upload - file too large",
          rawText: `File size: ${(file.size / 1024 / 1024).toFixed(2)} MB`,
          optionSelected: `File type: ${file.type || "unknown"}`,
        });

        createNotification({
          type: "error",
          message: `This file is too large to upload. The maximum supported file size is ${MAX_IMAGE_SIZE_MB} MB.`,
        });
        return;
      }

      if (file.size <= 0) {
        createNotification({
          type: "error",
          message:
            "This file seems to be empty. Please upload a different file.",
        });
        return;
      }

      try {
        const imageUrl = URL.createObjectURL(file);
        const position = screenToFlowPosition({ x: clientX, y: clientY });
        createImageNode({ imageUrl, position, file, uploadMethod });
      } catch {
        createNotification({
          type: "error",
          message:
            "Something went wrong while uploading. Please try again, or upload a different file if the issue continues.",
        });
      }
    },
    [
      screenToFlowPosition,
      createImageNode,
      createNotification,
      emitUserInteractionEvent,
    ],
  );

  // Handle drag & drop and paste functionality
  // Only handle if feature is enabled and we're not in view-only mode
  useEffect(() => {
    if (isViewOnlyMode) return;

    const handleDragEnter = (e: DragEvent) => {
      // Only track drag state for file drags to avoid interfering with ReactFlow's node dragging
      if (isFileDrag(e)) {
        dragCounterRef.current++;
        if (dragCounterRef.current === 1) {
          setIsDraggingFiles(true);
        }
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      // Only track drag state for file drags
      if (isFileDrag(e)) {
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
        if (dragCounterRef.current === 0) {
          setIsDraggingFiles(false);
        }
      }
    };

    const handleDragOver = (e: DragEvent) => {
      if (isFileDrag(e)) {
        // CRITICAL: Must call preventDefault() on dragover for drop event to fire
        // Only prevent default for file drags to avoid interfering with ReactFlow's node dragging
        e.preventDefault();
        // Set dropEffect to "copy" for better cursor feedback
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = "copy";
        }
      }
    };

    const handleDrop = (e: DragEvent) => {
      // Reset drag state when drop occurs
      dragCounterRef.current = 0;
      setIsDraggingFiles(false);

      const offset = 50;
      handleImageInsert(
        e,
        (e) => Array.from((e as DragEvent).dataTransfer?.files || []),
        (index) => ({
          x: e.clientX + index * offset,
          y: e.clientY + index * offset,
        }),
        handleImageFile,
        createNotification,
        emitUserInteractionEvent,
        "drag_and_drop",
      );
    };

    const handlePaste = (e: ClipboardEvent) => {
      // Skip if pasting into text inputs/editors
      // This is to prevent the image paste from interfering with the text paste
      const activeElement = document.activeElement;
      const isInTextInput = activeElement?.matches(
        "input, textarea, .remirror-editor, [contenteditable='true']",
      );

      if (isInTextInput) {
        return;
      }

      const offset = 50;
      handleImageInsert(
        e,
        (e) => {
          const clipboardData = (e as ClipboardEvent).clipboardData;
          return clipboardData ? Array.from(clipboardData.files || []) : null;
        },
        (index) => ({
          x: window.innerWidth / 2 + index * offset,
          y: window.innerHeight / 2 + index * offset,
        }),
        handleImageFile,
        createNotification,
        emitUserInteractionEvent,
        "copy_and_paste",
      );
    };

    const canvasElement = ref?.current;
    if (!canvasElement) return;

    canvasElement.addEventListener("dragenter", handleDragEnter);
    canvasElement.addEventListener("dragleave", handleDragLeave);
    canvasElement.addEventListener("dragover", handleDragOver);
    canvasElement.addEventListener("drop", handleDrop);
    // Paste events are keyboard-based and bubble from focused element, so listen on document
    document.addEventListener("paste", handlePaste);

    return () => {
      canvasElement.removeEventListener("dragenter", handleDragEnter);
      canvasElement.removeEventListener("dragleave", handleDragLeave);
      canvasElement.removeEventListener("dragover", handleDragOver);
      canvasElement.removeEventListener("drop", handleDrop);
      document.removeEventListener("paste", handlePaste);
      // Reset drag state on cleanup
      dragCounterRef.current = 0;
      setIsDraggingFiles(false);
    };
  }, [
    isViewOnlyMode,
    ref,
    handleImageFile,
    createNotification,
    emitUserInteractionEvent,
  ]);

  return isDraggingFiles;
}
